import { lazy } from "react";
import { Navigate, Route, Routes } from "react-router-dom";

import { AuthGate } from "./auth/AuthGate";
import { AuthProvider } from "./auth/AuthProvider";
import { Callback } from "./auth/Callback";
import { ProdConfirmationProvider } from "./auth/ProdConfirmationModal";
import { AppShell } from "./components/AppShell";

// Route-level code splitting: each screen is loaded on demand so it lands in
// its own Vite chunk instead of the entry bundle. Import directly from each
// feature dir (not the screens barrel) so the chunks stay separate. Suspense
// fallback lives in AppShell, wrapping the routed <Outlet>.
const AnalyticsScreen = lazy(
  () => import("./features/analytics/AnalyticsScreen"),
);
const AnnouncementsScreen = lazy(
  () => import("./features/announcements/AnnouncementsScreen"),
);
const BetaSignupsScreen = lazy(
  () => import("./features/beta-signups/BetaSignupsScreen"),
);
const CatalogScreen = lazy(() => import("./features/catalog/CatalogScreen"));
const CookSessionsScreen = lazy(
  () => import("./features/cook-sessions/CookSessionsScreen"),
);
const CreatorsScreen = lazy(() => import("./features/creators/CreatorsScreen"));
const EventsScreen = lazy(() => import("./features/events/EventsScreen"));
const FailedImportsScreen = lazy(
  () => import("./features/failed-imports/FailedImportsScreen"),
);
const FeedbackScreen = lazy(() => import("./features/feedback/FeedbackScreen"));
const InternalToolsScreen = lazy(
  () => import("./features/internal-tools/InternalToolsScreen"),
);
const JobsScreen = lazy(() => import("./features/jobs/JobsScreen"));
const ModerationScreen = lazy(
  () => import("./features/moderation/ModerationScreen"),
);
const NotificationsScreen = lazy(
  () => import("./features/notifications/NotificationsScreen"),
);
const OpsScreen = lazy(() => import("./features/ops/OpsScreen"));
const ProductLinksScreen = lazy(
  () => import("./features/product-links/ProductLinksScreen"),
);
const MakeItLinksScreen = lazy(
  () => import("./features/make-it-links/MakeItLinksScreen"),
);
const NutritionScreen = lazy(
  () => import("./features/nutrition/NutritionScreen"),
);
const PromoOffersScreen = lazy(
  () => import("./features/promo-offers/PromoOffersScreen"),
);
const RecipesScreen = lazy(() => import("./features/recipes/RecipesScreen"));
const SettingsScreen = lazy(() => import("./features/settings/SettingsScreen"));
const SkillsScreen = lazy(() => import("./features/skills/SkillsScreen"));
const UserTextScreen = lazy(
  () => import("./features/user-text/UserTextScreen"),
);
const UsersScreen = lazy(() => import("./features/users/UsersScreen"));

export default function App() {
  return (
    <AuthProvider>
      <ProdConfirmationProvider>
      <Routes>
        <Route path="/callback" element={<Callback />} />
        <Route
          element={
            <AuthGate>
              <AppShell />
            </AuthGate>
          }
        >
          <Route index element={<Navigate to="/creators" replace />} />
          <Route path="/creators" element={<CreatorsScreen />} />
          <Route path="/moderation" element={<ModerationScreen />} />
          <Route path="/feedback" element={<FeedbackScreen />} />
          <Route path="/recipes" element={<RecipesScreen />} />
          <Route path="/catalog" element={<CatalogScreen />} />
          <Route path="/skills" element={<SkillsScreen />} />
          <Route path="/product-links" element={<ProductLinksScreen />} />
          <Route path="/make-it-links" element={<MakeItLinksScreen />} />
          <Route path="/nutrition" element={<NutritionScreen />} />
          <Route path="/user-text" element={<UserTextScreen />} />
          <Route path="/cook-sessions" element={<CookSessionsScreen />} />
          {/* Back-compat: the standalone ingredients route now lives under Catalog. */}
          <Route
            path="/ingredients"
            element={<Navigate to="/catalog" replace />}
          />
          <Route path="/users" element={<UsersScreen />} />
          <Route path="/beta-signups" element={<BetaSignupsScreen />} />
          <Route path="/events" element={<EventsScreen />} />
          <Route path="/promo-offers" element={<PromoOffersScreen />} />
          <Route path="/announcements" element={<AnnouncementsScreen />} />
          <Route path="/notifications" element={<NotificationsScreen />} />
          <Route path="/internal-tools" element={<InternalToolsScreen />} />
          <Route path="/failed-imports" element={<FailedImportsScreen />} />
          <Route path="/analytics" element={<AnalyticsScreen />} />
          <Route path="/ops" element={<OpsScreen />} />
          <Route path="/jobs" element={<JobsScreen />} />
          <Route path="/settings" element={<SettingsScreen />} />
          <Route path="*" element={<Navigate to="/creators" replace />} />
        </Route>
      </Routes>
      </ProdConfirmationProvider>
    </AuthProvider>
  );
}
